import { useState, useEffect } from "react";
import api, { formatDate, whatsappHref, apiErrorMsg, ESTAGIOS } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Phone, MessageCircle, Mail, User as UserIcon, CalendarClock, ArrowRight, Send, Ban } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 42 (item #7): Modal profundo do retorno da Agenda.
 * Ao clicar num retorno, mostra:
 *  - Dados de contato completos da pessoa (tel/wa/email + link município)
 *  - Registrar Ligação / WhatsApp (com observação e novo retorno)
 *  - Mover o CARD EXISTENTE do funil (não duplica) — só se o backend
 *    encontrou um `card_id` associado ao município/curso da vendedora.
 *  - Cancelar (marcar como "Não quer") com motivo
 */
export default function AgendaRetornoDeepModal({ open, onClose, retorno, onDone }) {
  const [canal, setCanal] = useState("ligacao");
  const [observacao, setObservacao] = useState("");
  const [dataRetorno, setDataRetorno] = useState("");
  const [saving, setSaving] = useState(false);
  const [novoEstagio, setNovoEstagio] = useState("");
  const [motivosMap, setMotivosMap] = useState({});
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivoCat, setMotivoCat] = useState("");
  const [motivoSub, setMotivoSub] = useState("");
  const [motivoObs, setMotivoObs] = useState("");

  useEffect(() => {
    if (!open) return;
    setCanal(retorno?.canal || "ligacao");
    setObservacao("");
    setDataRetorno("");
    setNovoEstagio(retorno?.card_estagio || "");
    setMotivoCat(""); setMotivoSub(""); setMotivoObs("");
    api.get("/motivos-perda").then((r) => setMotivosMap(r.data || {})).catch(() => {});
  }, [open, retorno]);

  if (!retorno) return null;

  const registrarContato = async () => {
    if (!observacao.trim()) { toast.error("Escreva uma observação do contato"); return; }
    setSaving(true);
    try {
      // Cria novo contato + marca este retorno como concluído
      await api.post("/contatos", {
        pessoa_id: retorno.pessoa_id,
        municipio_id: retorno.municipio_id,
        canal,
        observacao: observacao.trim(),
        data_retorno: dataRetorno || null,
      });
      await api.patch(`/contatos/${retorno.id}`, { retorno_concluido: true });
      toast.success("Retorno registrado ✓");
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao registrar contato"));
    } finally { setSaving(false); }
  };

  const moverCardComMotivo = async () => {
    if (!motivoCat || !motivoSub) { toast.error("Selecione categoria e motivo"); return; }
    setSaving(true);
    try {
      await api.patch(`/cards/${retorno.card_id}`, {
        estagio: "nao_querem",
        motivo_perda_categoria: motivoCat,
        motivo_perda_sub: motivoSub,
        motivo_perda_obs: motivoObs || null,
      });
      toast.success("Card movido para Negativas");
      setMotivoOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao mover"));
    } finally { setSaving(false); }
  };

  const moverCard = async () => {
    if (!retorno.card_id) { toast.error("Município não tem card ativo no funil"); return; }
    if (!novoEstagio || novoEstagio === retorno.card_estagio) return;
    if (novoEstagio === "nao_querem") {
      setMotivoOpen(true);
      return;
    }
    setSaving(true);
    try {
      const estagioLabelStr = ESTAGIOS.find((e) => e.key === novoEstagio)?.label || novoEstagio;
      await api.patch(`/cards/${retorno.card_id}`, { estagio: novoEstagio });
      toast.success(`Card movido para ${estagioLabelStr}`);
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao mover"));
    } finally { setSaving(false); }
  };

  const estagioLabel = (k) => ESTAGIOS.find((e) => e.key === k)?.label || k;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agenda-retorno-deep-modal">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <UserIcon className="w-5 h-5 text-blue-600" />
            <span className="font-display text-2xl">{retorno.pessoa_nome}</span>
            <span className="text-sm text-slate-500 font-normal">— {retorno.pessoa_funcao}</span>
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <b>{retorno.municipio_nome}</b> / {retorno.municipio_uf}
            {retorno.card_estagio && (
              <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                Estágio atual: <b>{estagioLabel(retorno.card_estagio)}</b>
              </span>
            )}
            <span className="text-slate-400">·</span>
            <CalendarClock className="w-3 h-3" /> Retorno para {formatDate(retorno.data_retorno + "T12:00:00")}
          </div>
        </DialogHeader>

        {/* Contato rápido */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-3">
          {retorno.pessoa_telefone && (
            <a
              href={`tel:${retorno.pessoa_telefone}`}
              className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-lg px-3 py-2 text-sm font-semibold"
              data-testid="retorno-btn-tel"
            >
              <Phone className="w-4 h-4" /> {retorno.pessoa_telefone}
            </a>
          )}
          {retorno.pessoa_whatsapp && (
            <a
              href={whatsappHref(retorno.pessoa_whatsapp)}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2 text-sm font-semibold"
              data-testid="retorno-btn-wa"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {retorno.pessoa_email && (
            <a
              href={`mailto:${retorno.pessoa_email}`}
              className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-lg px-3 py-2 text-sm font-semibold truncate"
              data-testid="retorno-btn-email"
            >
              <Mail className="w-4 h-4" /> <span className="truncate">{retorno.pessoa_email}</span>
            </a>
          )}
        </div>

        {/* Registrar contato */}
        <div className="mt-4 border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="text-[11px] uppercase font-bold tracking-widest text-slate-500">Registrar follow-up</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Canal</Label>
              <Select value={canal} onValueChange={setCanal}>
                <SelectTrigger data-testid="retorno-canal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ligacao">📞 Ligação</SelectItem>
                  <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Novo retorno em (opcional)</Label>
              <Input
                type="date"
                value={dataRetorno}
                onChange={(e) => setDataRetorno(e.target.value)}
                data-testid="retorno-nova-data"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Observação *</Label>
            <Textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={3}
              placeholder="O que foi conversado..."
              data-testid="retorno-obs"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={registrarContato}
              disabled={saving || !observacao.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="retorno-btn-salvar"
            >
              <Send className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Registrar & Concluir retorno"}
            </Button>
          </div>
        </div>

        {/* Mover no funil (SEM duplicar) */}
        {retorno.card_id ? (
          <div className="mt-4 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retorno-mover-funil">
            <div className="text-[11px] uppercase font-bold tracking-widest text-slate-500">
              Mover este município no funil <span className="text-slate-400 font-normal">(sem duplicar)</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Novo estágio</Label>
                <Select value={novoEstagio} onValueChange={setNovoEstagio}>
                  <SelectTrigger data-testid="retorno-novo-estagio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTAGIOS.filter((e) => e.key !== "inscrito").map((e) => (
                      <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={moverCard}
                disabled={saving || !novoEstagio || novoEstagio === retorno.card_estagio}
                className={novoEstagio === "nao_querem" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
                data-testid="retorno-btn-mover"
              >
                {novoEstagio === "nao_querem" ? <Ban className="w-4 h-4 mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                Mover
              </Button>
            </div>
            <div className="text-[11px] text-slate-500">
              Para <b>inscrever oficialmente</b>, use o menu <b>Registrar Inscrição</b> no Funil.
            </div>
          </div>
        ) : (
          <div className="mt-4 text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 text-slate-600">
            Este município ainda não tem um card ativo no funil da vendedora. Use o menu <b>Enviar para curso</b> na Carteira para começar.
          </div>
        )}

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>

        {/* Sub-modal motivo de perda quando "não quer" */}
        <Dialog open={motivoOpen} onOpenChange={(o) => !o && setMotivoOpen(false)}>
          <DialogContent data-testid="retorno-motivo-dialog">
            <DialogHeader>
              <DialogTitle>Por que {retorno.municipio_nome} não vai comprar?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Categoria *</Label>
                <Select value={motivoCat} onValueChange={(v) => { setMotivoCat(v); setMotivoSub(""); }}>
                  <SelectTrigger data-testid="retorno-motivo-cat"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(motivosMap).map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {motivoCat && (
                <div>
                  <Label className="text-xs">Motivo específico *</Label>
                  <Select value={motivoSub} onValueChange={setMotivoSub}>
                    <SelectTrigger data-testid="retorno-motivo-sub"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {(motivosMap[motivoCat] || []).map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Observação (opcional)</Label>
                <Input value={motivoObs} onChange={(e) => setMotivoObs(e.target.value)} data-testid="retorno-motivo-obs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMotivoOpen(false)}>Cancelar</Button>
              <Button onClick={moverCardComMotivo} className="bg-red-600 hover:bg-red-700" data-testid="retorno-motivo-salvar">
                Registrar & Mover
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
